import { useEventListener } from "@vueuse/core";
import { onMounted, onUnmounted } from "vue";
import { RECONNECTED_EVENT } from "#ce/framework/events";
import { toBoolean } from "#ce/utils/props";

/**
 * The flash alerts currently connected, in connection order.
 *
 * @type {Set<HTMLElement>}
 */
const connected = new Set();

/**
 * Keeps one flash alert on screen: on every connection the alert removes the flash alerts that
 * connected before it. Producers do not know about each other and `:host([flash])` pins them all
 * to the same point, so the newest one is the only one a reader could see anyway.
 *
 * Clearing on connection rather than on mount is what makes a batch resolve to its last entry.
 * A pre-rendered batch upgrades in document order, and its later entries are still raw elements
 * when the first one mounts, so a document query would remove them before they ever connected.
 *
 * @param {Object} props - The alert's props.
 * @param {HTMLElement|null} host - The custom element host.
 */
export function useFlashSingleton(props, host) {
  function clearEarlierFlashes() {
    if (!host || !toBoolean(props.flash)) {
      return;
    }

    connected.forEach((alert) => {
      if (alert === host) {
        return;
      }

      connected.delete(alert);
      // The same event the alert emits when a reader closes it, so a producer holding the
      // alert in reactive state drops its entry instead of keeping one Vue no longer owns.
      // Vue carries an emit's arguments in `detail`, so a listener reading it finds the
      // empty list a no-argument emit would give it.
      alert.dispatchEvent(new CustomEvent("close", { detail: [] }));
      alert.remove();
    });

    connected.add(host);
  }

  onMounted(clearEarlierFlashes);
  onUnmounted(() => connected.delete(host));
  // Vue runs nothing when a mounted instance is detached and re-inserted in the same tick.
  useEventListener(host, RECONNECTED_EVENT, clearEarlierFlashes);
}
